import { proxyCustomElement, HTMLElement, h } from '@stencil/core/internal/client';
import { g as getWindow, a as getDocumentElement, i as isLayoutViewport, b as getWindowScrollBarX, d as getWindowScroll, e as getComputedStyle, m as max, f as isShadowRoot, v as viewport, h as isElement, j as min, k as getBoundingClientRect, l as listScrollParents, n as getParentNode, p as isHTMLElement, q as getOffsetParent, r as getNodeName, s as basePlacements, t as computeOffsets, u as popper, w as clippingParents, x as right, y as bottom, z as top, A as reference, B as getVariation, C as variationPlacements, D as getBasePlacement, E as placements, F as auto, G as start, H as left, c as createPopper, o as offset } from './popper-lite.js';

function getViewportRect(element, strategy) {
  var win = getWindow(element);
  var html = getDocumentElement(element);
  var visualViewport = win.visualViewport;
  var width = html.clientWidth;
  var height = html.clientHeight;
  var x = 0;
  var y = 0;

  if (visualViewport) {
    width = visualViewport.width;
    height = visualViewport.height;
    var layoutViewport = isLayoutViewport();

    if (layoutViewport || !layoutViewport && strategy === 'fixed') {
      x = visualViewport.offsetLeft;
      y = visualViewport.offsetTop;
    }
  }

  return {
    width: width,
    height: height,
    x: x + getWindowScrollBarX(element),
    y: y
  };
}

// of the `<html>` and `<body>` rect bounds if horizontally scrollable

function getDocumentRect(element) {
  var _element$ownerDocumen;

  var html = getDocumentElement(element);
  var winScroll = getWindowScroll(element);
  var body = (_element$ownerDocumen = element.ownerDocument) == null ? void 0 : _element$ownerDocumen.body;
  var width = max(html.scrollWidth, html.clientWidth, body ? body.scrollWidth : 0, body ? body.clientWidth : 0);
  var height = max(html.scrollHeight, html.clientHeight, body ? body.scrollHeight : 0, body ? body.clientHeight : 0);
  var x = -winScroll.scrollLeft + getWindowScrollBarX(element);
  var y = -winScroll.scrollTop;

  if (getComputedStyle(body || html).direction === 'rtl') {
    x += max(html.clientWidth, body ? body.clientWidth : 0) - width;
  }

  return {
    width: width,
    height: height,
    x: x,
    y: y
  };
}

function contains(parent, child) {
  var rootNode = child.getRootNode && child.getRootNode(); // First, attempt with faster native method

  if (parent.contains(child)) {
    return true;
  } // then fallback to custom implementation with Shadow DOM support
  else if (rootNode && isShadowRoot(rootNode)) {
      var next = child;

      do {
        if (next && parent.isSameNode(next)) {
          return true;
        } // $FlowFixMe[prop-missing]: need a better way to handle this...


        next = next.parentNode || next.host;
      } while (next);
    } // Give up, the result is false


  return false;
}

function rectToClientRect(rect) {
  return Object.assign({}, rect, {
    left: rect.x,
    top: rect.y,
    right: rect.x + rect.width,
    bottom: rect.y + rect.height
  });
}

function getInnerBoundingClientRect(element, strategy) {
  var rect = getBoundingClientRect(element, false, strategy === 'fixed');
  rect.top = rect.top + element.clientTop;
  rect.left = rect.left + element.clientLeft;
  rect.bottom = rect.top + element.clientHeight;
  rect.right = rect.left + element.clientWidth;
  rect.width = element.clientWidth;
  rect.height = element.clientHeight;
  rect.x = rect.left;
  rect.y = rect.top;
  return rect;
}

function getClientRectFromMixedType(element, clippingParent, strategy) {
  return clippingParent === viewport ? rectToClientRect(getViewportRect(element, strategy)) : isElement(clippingParent) ? getInnerBoundingClientRect(clippingParent, strategy) : rectToClientRect(getDocumentRect(getDocumentElement(element)));
} // A "clipping parent" is an overflowable container with the characteristic of
// clipping (or hiding) overflowing elements with a position different from
// `initial`


function getClippingParents(element) {
  var clippingParents = listScrollParents(getParentNode(element));
  var canEscapeClipping = ['absolute', 'fixed'].indexOf(getComputedStyle(element).position) >= 0;
  var clipperElement = canEscapeClipping && isHTMLElement(element) ? getOffsetParent(element) : element;

  if (!isElement(clipperElement)) {
    return [];
  } // $FlowFixMe[incompatible-return]: https://github.com/facebook/flow/issues/1414


  return clippingParents.filter(function (clippingParent) {
    return isElement(clippingParent) && contains(clippingParent, clipperElement) && getNodeName(clippingParent) !== 'body';
  });
} // Gets the maximum area that the element is visible in due to any number of
// clipping parents


function getClippingRect(element, boundary, rootBoundary, strategy) {
  var mainClippingParents = boundary === 'clippingParents' ? getClippingParents(element) : [].concat(boundary);
  var clippingParents = [].concat(mainClippingParents, [rootBoundary]);
  var firstClippingParent = clippingParents[0];
  var clippingRect = clippingParents.reduce(function (accRect, clippingParent) {
    var rect = getClientRectFromMixedType(element, clippingParent, strategy);
    accRect.top = max(rect.top, accRect.top);
    accRect.right = min(rect.right, accRect.right);
    accRect.bottom = min(rect.bottom, accRect.bottom);
    accRect.left = max(rect.left, accRect.left);
    return accRect;
  }, getClientRectFromMixedType(element, firstClippingParent, strategy));
  clippingRect.width = clippingRect.right - clippingRect.left;
  clippingRect.height = clippingRect.bottom - clippingRect.top;
  clippingRect.x = clippingRect.left;
  clippingRect.y = clippingRect.top;
  return clippingRect;
}

function getFreshSideObject() {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  };
}

function mergePaddingObject(paddingObject) {
  return Object.assign({}, getFreshSideObject(), paddingObject);
}

function expandToHashMap(value, keys) {
  return keys.reduce(function (hashMap, key) {
    hashMap[key] = value;
    return hashMap;
  }, {});
}

function detectOverflow(state, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      _options$placement = _options.placement,
      placement = _options$placement === void 0 ? state.placement : _options$placement,
      _options$strategy = _options.strategy,
      strategy = _options$strategy === void 0 ? state.strategy : _options$strategy,
      _options$boundary = _options.boundary,
      boundary = _options$boundary === void 0 ? clippingParents : _options$boundary,
      _options$rootBoundary = _options.rootBoundary,
      rootBoundary = _options$rootBoundary === void 0 ? viewport : _options$rootBoundary,
      _options$elementConte = _options.elementContext,
      elementContext = _options$elementConte === void 0 ? popper : _options$elementConte,
      _options$altBoundary = _options.altBoundary,
      altBoundary = _options$altBoundary === void 0 ? false : _options$altBoundary,
      _options$padding = _options.padding,
      padding = _options$padding === void 0 ? 0 : _options$padding;
  var paddingObject = mergePaddingObject(typeof padding !== 'number' ? padding : expandToHashMap(padding, basePlacements));
  var altContext = elementContext === popper ? reference : popper;
  var popperRect = state.rects.popper;
  var element = state.elements[altBoundary ? altContext : elementContext];
  var clippingClientRect = getClippingRect(isElement(element) ? element : element.contextElement || getDocumentElement(state.elements.popper), boundary, rootBoundary, strategy);
  var referenceClientRect = getBoundingClientRect(state.elements.reference);
  var popperOffsets = computeOffsets({
    reference: referenceClientRect,
    element: popperRect,
    strategy: 'absolute',
    placement: placement
  });
  var popperClientRect = rectToClientRect(Object.assign({}, popperRect, popperOffsets));
  var elementClientRect = elementContext === popper ? popperClientRect : referenceClientRect; // positive = overflowing the clipping rect
  // 0 or negative = within the clipping rect

  var overflowOffsets = {
    top: clippingClientRect.top - elementClientRect.top + paddingObject.top,
    bottom: elementClientRect.bottom - clippingClientRect.bottom + paddingObject.bottom,
    left: clippingClientRect.left - elementClientRect.left + paddingObject.left,
    right: elementClientRect.right - clippingClientRect.right + paddingObject.right
  };
  var offsetData = state.modifiersData.offset; // Offsets can be applied only to the popper element

  if (elementContext === popper && offsetData) {
    var offset = offsetData[placement];
    Object.keys(overflowOffsets).forEach(function (key) {
      var multiply = [right, bottom].indexOf(key) >= 0 ? 1 : -1;
      var axis = [top, bottom].indexOf(key) >= 0 ? 'y' : 'x';
      overflowOffsets[key] += offset[axis] * multiply;
    });
  }

  return overflowOffsets;
}

var hash$1 = {
  left: 'right',
  right: 'left',
  bottom: 'top',
  top: 'bottom'
};
function getOppositePlacement(placement) {
  return placement.replace(/left|right|bottom|top/g, function (matched) {
    return hash$1[matched];
  });
}

var hash = {
  start: 'end',
  end: 'start'
};
function getOppositeVariationPlacement(placement) {
  return placement.replace(/start|end/g, function (matched) {
    return hash[matched];
  });
}

function computeAutoPlacement(state, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      placement = _options.placement,
      boundary = _options.boundary,
      rootBoundary = _options.rootBoundary,
      padding = _options.padding,
      flipVariations = _options.flipVariations,
      _options$allowedAutoP = _options.allowedAutoPlacements,
      allowedAutoPlacements = _options$allowedAutoP === void 0 ? placements : _options$allowedAutoP;
  var variation = getVariation(placement);
  var placements$1 = variation ? flipVariations ? variationPlacements : variationPlacements.filter(function (placement) {
    return getVariation(placement) === variation;
  }) : basePlacements;
  var allowedPlacements = placements$1.filter(function (placement) {
    return allowedAutoPlacements.indexOf(placement) >= 0;
  });

  if (allowedPlacements.length === 0) {
    allowedPlacements = placements$1;
  } // $FlowFixMe[incompatible-type]: Flow seems to have problems with two array unions...


  var overflows = allowedPlacements.reduce(function (acc, placement) {
    acc[placement] = detectOverflow(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      padding: padding
    })[getBasePlacement(placement)];
    return acc;
  }, {});
  return Object.keys(overflows).sort(function (a, b) {
    return overflows[a] - overflows[b];
  });
}

function getExpandedFallbackPlacements(placement) {
  if (getBasePlacement(placement) === auto) {
    return [];
  }

  var oppositePlacement = getOppositePlacement(placement);
  return [getOppositeVariationPlacement(placement), oppositePlacement, getOppositeVariationPlacement(oppositePlacement)];
}

function flip(_ref) {
  var state = _ref.state,
      options = _ref.options,
      name = _ref.name;

  if (state.modifiersData[name]._skip) {
    return;
  }

  var _options$mainAxis = options.mainAxis,
      checkMainAxis = _options$mainAxis === void 0 ? true : _options$mainAxis,
      _options$altAxis = options.altAxis,
      checkAltAxis = _options$altAxis === void 0 ? true : _options$altAxis,
      specifiedFallbackPlacements = options.fallbackPlacements,
      padding = options.padding,
      boundary = options.boundary,
      rootBoundary = options.rootBoundary,
      altBoundary = options.altBoundary,
      _options$flipVariatio = options.flipVariations,
      flipVariations = _options$flipVariatio === void 0 ? true : _options$flipVariatio,
      allowedAutoPlacements = options.allowedAutoPlacements;
  var preferredPlacement = state.options.placement;
  var basePlacement = getBasePlacement(preferredPlacement);
  var isBasePlacement = basePlacement === preferredPlacement;
  var fallbackPlacements = specifiedFallbackPlacements || (isBasePlacement || !flipVariations ? [getOppositePlacement(preferredPlacement)] : getExpandedFallbackPlacements(preferredPlacement));
  var placements = [preferredPlacement].concat(fallbackPlacements).reduce(function (acc, placement) {
    return acc.concat(getBasePlacement(placement) === auto ? computeAutoPlacement(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      padding: padding,
      flipVariations: flipVariations,
      allowedAutoPlacements: allowedAutoPlacements
    }) : placement);
  }, []);
  var referenceRect = state.rects.reference;
  var popperRect = state.rects.popper;
  var checksMap = new Map();
  var makeFallbackChecks = true;
  var firstFittingPlacement = placements[0];

  for (var i = 0; i < placements.length; i++) {
    var placement = placements[i];

    var _basePlacement = getBasePlacement(placement);

    var isStartVariation = getVariation(placement) === start;
    var isVertical = [top, bottom].indexOf(_basePlacement) >= 0;
    var len = isVertical ? 'width' : 'height';
    var overflow = detectOverflow(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      altBoundary: altBoundary,
      padding: padding
    });
    var mainVariationSide = isVertical ? isStartVariation ? right : left : isStartVariation ? bottom : top;

    if (referenceRect[len] > popperRect[len]) {
      mainVariationSide = getOppositePlacement(mainVariationSide);
    }

    var altVariationSide = getOppositePlacement(mainVariationSide);
    var checks = [];

    if (checkMainAxis) {
      checks.push(overflow[_basePlacement] <= 0);
    }

    if (checkAltAxis) {
      checks.push(overflow[mainVariationSide] <= 0, overflow[altVariationSide] <= 0);
    }

    if (checks.every(function (check) {
      return check;
    })) {
      firstFittingPlacement = placement;
      makeFallbackChecks = false;
      break;
    }

    checksMap.set(placement, checks);
  }

  if (makeFallbackChecks) {
    // `2` may be desired in some cases – research later
    var numberOfChecks = flipVariations ? 3 : 1;

    var _loop = function _loop(_i) {
      var fittingPlacement = placements.find(function (placement) {
        var checks = checksMap.get(placement);

        if (checks) {
          return checks.slice(0, _i).every(function (check) {
            return check;
          });
        }
      });

      if (fittingPlacement) {
        firstFittingPlacement = fittingPlacement;
        return "break";
      }
    };

    for (var _i = numberOfChecks; _i > 0; _i--) {
      var _ret = _loop(_i);

      if (_ret === "break") break;
    }
  }

  if (state.placement !== firstFittingPlacement) {
    state.modifiersData[name]._skip = true;
    state.placement = firstFittingPlacement;
    state.reset = true;
  }
} // eslint-disable-next-line import/no-unused-modules


const flip$1 = {
  name: 'flip',
  enabled: true,
  phase: 'main',
  fn: flip,
  requiresIfExists: ['offset'],
  data: {
    _skip: false
  }
};

const tooltipCss = ":host{box-sizing:border-box;font-family:Inter, sans-serif}.wrapper{position:relative;display:none;z-index:var(--z-index-popover, 9100)}.wrapper.show{display:block}.wrapper .tooltip{position:absolute;max-width:var(--tooltip-width, 200px);border-radius:4px;background-color:var(--hmc-color-notification-info-strong-background, var(--hmc-color-content-default, var(--hmc-color-gray-black, #101820)));padding:var(--tooltip-padding, 8px 12px);font-family:var(--font-family-body, \"Inter\", sans-serif);font-size:var(--font-size-default, 14px);line-height:20px;text-align:left;font-weight:normal;word-wrap:break-word;color:var(--hmc-color-notification-info-strong-text, var(--hmc-color-content-inverse, var(--hmc-color-gray-white, #fff)));width:max-content}.wrapper .tooltip.xs{font-size:var(--font-size-small, 12px);line-height:16px}.wrapper .tooltip.xs .title{font-size:var(--font-size-small, 12px);line-height:20px}.wrapper .tooltip.toggle{padding:var(--tooltip-padding, 10px 16px 16px)}.wrapper .tooltip .title{font-weight:700;font-size:var(--font-size-medium, 16px);line-height:24px}.wrapper.fixed-width .tooltip{width:var(--tooltip-width, 200px)}#arrow,#arrow::before{position:absolute;width:8px;height:8px;z-index:-1}#arrow::before{content:\"\";transform:rotate(45deg);background-color:var(--hmc-color-notification-info-strong-background, var(--hmc-color-content-default, var(--hmc-color-gray-black, #101820)));border-radius:1px}.wrapper[data-popper-placement=top]>.tooltip,.wrapper[data-popper-placement=top]>#arrow,.wrapper[data-popper-placement=bottom]>.tooltip,.wrapper[data-popper-placement=bottom]>#arrow{left:50%;transform:translateX(-50%)}.wrapper[data-popper-placement=top-start]>.tooltip,.wrapper[data-popper-placement=bottom-start]>.tooltip{left:100%}.wrapper[data-popper-placement=top-start]>#arrow,.wrapper[data-popper-placement=bottom-start]>#arrow{left:16px}.wrapper[data-popper-placement=top-end]>.tooltip,.wrapper[data-popper-placement=bottom-end]>.tooltip{right:100%}.wrapper[data-popper-placement=top-end]>#arrow,.wrapper[data-popper-placement=bottom-end]>#arrow{right:16px}.wrapper[data-popper-placement^=top]>.tooltip{bottom:calc(100% - var(--tooltip-y-offset, 0px))}.wrapper[data-popper-placement^=bottom]>.tooltip{top:calc(100% + var(--tooltip-y-offset, 0px))}.wrapper[data-popper-placement^=top] #arrow,.wrapper[data-popper-placement^=bottom] #arrow{bottom:calc(-4px - var(--tooltip-y-offset, 0px))}.wrapper[data-popper-placement^=top] #arrow::before,.wrapper[data-popper-placement^=bottom] #arrow::before{border-radius:1px 0}.wrapper[data-popper-placement=left] .tooltip,.wrapper[data-popper-placement=left] #arrow,.wrapper[data-popper-placement=right] .tooltip,.wrapper[data-popper-placement=right] #arrow{top:50%;transform:translateY(calc(-50% + var(--tooltip-y-offset, 0px)))}.wrapper[data-popper-placement=left] #arrow::before,.wrapper[data-popper-placement=right] #arrow::before{border-radius:0 1px}.wrapper[data-popper-placement^=left]>.tooltip{right:100%}.wrapper[data-popper-placement^=left]>#arrow,.wrapper[data-popper-placement^=right]>#arrow{right:-4px}.wrapper[data-popper-placement$=start]>.tooltip,.wrapper[data-popper-placement$=end]>.tooltip{transform:unset}";

const HmcTooltip$1 = /*@__PURE__*/ proxyCustomElement(class HmcTooltip extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.selectTooltipTarget = () => {
      var _a;
      if (this.triggerId !== '') {
        const rootElement = this.el.getRootNode();
        this.triggerElement =
          (_a = document.getElementById(this.triggerId)) !== null && _a !== void 0 ? _a : rootElement.querySelector(`#${this.triggerId}`);
      }
    };
    this.handleTooltip = () => {
      if (this.isOpen) {
        const xoffset = this.placement.endsWith('end')
          ? 12
          : this.placement.endsWith('start')
            ? -12
            : 0;
        this.tooltipInstance = createPopper(this.triggerElement, this.tooltipEl, {
          placement: this.placement,
          strategy: 'absolute',
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [xoffset, 8],
              },
            },
            {
              name: 'flip',
              options: {
                flipVariations: false,
              },
            },
            flip$1,
            offset,
          ],
        });
      }
      else if (this.tooltipInstance) {
        this.tooltipInstance.destroy();
        this.tooltipInstance = null;
      }
    };
    this.triggerId = undefined;
    this.placement = 'bottom';
    this.toggleTitle = '';
    this.isOpen = false;
    this.isFixedWidth = false;
    this.isToggle = false;
    this.size = 'sm';
  }
  updateOnTriggerIdChange() {
    this.selectTooltipTarget();
  }
  closeTooltip(ev) {
    if (this.isOpen && (ev.code === 'Esc' || ev.code === 'Escape')) {
      this.isOpen = false;
    }
  }
  componentDidUpdate() {
    this.handleTooltip();
  }
  componentDidLoad() {
    this.selectTooltipTarget();
    if (this.triggerElement) {
      if (this.isToggle) {
        this.triggerElement.addEventListener('click', ev => {
          ev.stopPropagation();
          this.isOpen = !this.isOpen;
        });
      }
      else {
        this.triggerElement.addEventListener('mouseenter', ev => {
          ev.stopPropagation();
          this.isOpen = true;
        });
        this.triggerElement.addEventListener('mouseleave', ev => {
          ev.stopPropagation();
          this.isOpen = false;
        });
        this.triggerElement.addEventListener('focusin', ev => {
          ev.stopPropagation();
          this.isOpen = true;
        });
        this.triggerElement.addEventListener('focusout', ev => {
          ev.stopPropagation();
          this.isOpen = false;
        });
      }
      this.handleTooltip();
    }
  }
  render() {
    const { isOpen, isFixedWidth, isToggle, toggleTitle, size } = this;
    const wrapperClasses = {
      'wrapper': true,
      'show': isOpen,
      'fixed-width': isFixedWidth,
    };
    const tooltipClasses = {
      tooltip: true,
      toggle: isToggle,
      [size]: true,
    };
    return (h("div", { class: wrapperClasses, ref: ref => (this.tooltipEl = ref) }, h("div", { class: tooltipClasses }, h("div", { class: "title" }, toggleTitle), h("slot", null)), h("div", { id: "arrow" })));
  }
  get el() { return this; }
  static get watchers() { return {
    "triggerId": ["updateOnTriggerIdChange"]
  }; }
  static get style() { return tooltipCss; }
}, [1, "hmc-tooltip", {
    "triggerId": [1, "trigger-id"],
    "placement": [1],
    "toggleTitle": [1, "toggle-title"],
    "isOpen": [4, "open"],
    "isFixedWidth": [4, "fixed-width"],
    "isToggle": [4, "toggle"],
    "size": [1]
  }, [[4, "keydown", "closeTooltip"]]]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["hmc-tooltip"];
  components.forEach(tagName => { switch (tagName) {
    case "hmc-tooltip":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, HmcTooltip$1);
      }
      break;
  } });
}

const HmcTooltip = HmcTooltip$1;
const defineCustomElement = defineCustomElement$1;

export { HmcTooltip, defineCustomElement };
